import { loadStripe, StripeCardElementChangeEvent } from "@stripe/stripe-js";
import {
  WSButton,
  WSStripeInput,
  WSStripeInputRef,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PayoutPreferences,
  VerificationStatus
} from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo, useRef, useState } from "react";
import { SetupFlowRouteComponentProps } from "../../../components/SetupFlow/SetupFlow";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  QUERY_INSTANT_PAYOUT,
  QUERY_PAYMENTS_STATUS
} from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { PaymentsSetupLayout } from "../../components/PaymentsSetupLayout/PaymentsSetupLayout";
import { useVerificationUpgrade } from "../../components/VerificationUpgrade/VerificationUpgrade";

export const SetupPayoutsInstant: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const userId = useUserId();
  const stripeInput = useRef<WSStripeInputRef>(null);
  const stripePromise = useMemo(
    () => loadStripe(`${process.env.REACT_APP_STRIPE_KEY}`),
    []
  );
  const [stripeInputState, setStripeInputState] = useState<
    StripeCardElementChangeEvent | undefined
  >();
  const featureFlagsQuery = useFeatureFlags();
  const { requestVerification } = useVerificationUpgrade();

  const [submit, submitMeta] = useWSMutation(
    async () => {
      if (stripeInputState?.complete) {
        const result = await stripeInput?.current?.createCardToken({
          currency: "USD"
        });
        if (result?.token) {
          if (featureFlagsQuery.data?.verificationsByFeature) {
            await requestVerification({ cardPayments: true });
          } else {
            await paymentsService.service.update(userId, {
              verifications: {
                taxDocumentation: VerificationStatus.Verified,
                banking: VerificationStatus.Verified,
                cardPayments: VerificationStatus.Verified
              }
            });
          }

          const instantPayoutAccount = await paymentsService.banking.instantPayout.create(
            {
              externalPayoutAccountToken: result.token.id
            }
          );

          await paymentsService.payoutSettings.update(userId, {
            payoutPreferences: PayoutPreferences.Instant,
            instant: {
              destinationType: PayoutDestinationType.Card,
              destinationId: instantPayoutAccount.instantPayoutAccountId
            }
          });

          onNext();
        } else {
          throw new Error("No token generated");
        }
      } else {
        throw new Error("Invalid card data");
      }
    },
    {
      dependencies: [QUERY_PAYMENTS_STATUS, QUERY_INSTANT_PAYOUT],
      onSuccess: onNext
    }
  );

  return (
    <PaymentsSetupLayout
      onBack={onBack}
      title="Connect your debit card for instant payouts"
    >
      <WSText mb="M">
        Instantly receive funds to a supported debit card as soon as the funds
        are received. Income typically appear in the associated bank account
        within 30 minutes.
      </WSText>
      <WSText mb="M">
        Instant payouts cost 1% of the payout amount, with a minimum fee of
        $0.50.
      </WSText>
      <WSText mb="M">
        Most U.S. banks issue debit cards are compatible with Instant Payouts.
      </WSText>

      <WSStripeInput
        ref={stripeInput}
        stripeInstance={stripePromise}
        onInputChange={setStripeInputState}
        name="creditCard"
        mb="M"
      />

      <WSErrorMessage
        error={submitMeta.error}
        contextKey="InstantDepositAccount"
        mb="XS"
      />
      {submitMeta.error && (
        <WSButton.Link onClick={onBack} rightIcon="arrowRight" mb="M">
          Try another payout method
        </WSButton.Link>
      )}

      <WSButton
        mt="M"
        name="continue"
        onClick={submit}
        loading={submitMeta.isLoading}
        fullWidth
      >
        Continue
      </WSButton>
    </PaymentsSetupLayout>
  );
};
